import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation } from "wouter";

export default function WebsiteTerms() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    document.title = "Website Terms | The Perth Bookkeeper";
    // Set canonical tag
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link') as HTMLLinkElement;
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = 'https://theperthbookkeeper.com.au/website-terms';
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-[oklch(0.16_0.05_250)]">
      {/* Header Section */}
      <div className="bg-gradient-to-b from-[oklch(0.20_0.06_250)] to-[oklch(0.16_0.05_250)] border-b border-white/10">
        <div className="container py-12 lg:py-16">
          <button
            onClick={() => setLocation("/")}
            className="inline-flex items-center gap-2 text-[oklch(0.72_0.12_75)] hover:text-[oklch(0.72_0.12_75)]/80 transition-colors mb-6 font-medium text-sm"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Website Terms & Conditions
          </h1>
          <p className="text-white/60 text-lg max-w-2xl" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Terms and conditions for using The Perth Bookkeeper website
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="container py-12 lg:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[oklch(0.18_0.05_250)] rounded-lg border border-white/10 p-8 lg:p-12 text-white/90" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            
            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                1. Terms of Use
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                By accessing and using the theperthbookkeeper.com.au website (the "Website"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
              <p className="text-white/80 leading-relaxed">
                The Perth Bookkeeper reserves the right to make changes to these Terms and Conditions at any time. Your continued use of the Website following the posting of revised Terms and Conditions means that you accept and agree to the changes.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                2. Use License
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                Permission is granted to temporarily download one copy of the materials (information or software) on The Perth Bookkeeper's Website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/80 mb-4">
                <li>Modifying or copying the materials</li>
                <li>Using the materials for any commercial purpose or for any public display</li>
                <li>Attempting to decompile or reverse engineer any software contained on the Website</li>
                <li>Removing any copyright or other proprietary notations from the materials</li>
                <li>Transferring the materials to another person or "mirroring" the materials on any other server</li>
              </ul>
              <p className="text-white/80 leading-relaxed">
                This license shall automatically terminate if you violate any of these restrictions and may be terminated by The Perth Bookkeeper at any time. Upon terminating your viewing of these materials or upon the termination of this license, you must destroy any downloaded materials in your possession whether in electronic or printed format.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                3. Disclaimer
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                The materials on The Perth Bookkeeper's Website are provided on an 'as is' basis. The Perth Bookkeeper makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
              </p>
              <p className="text-white/80 leading-relaxed">
                Further, The Perth Bookkeeper does not warrant or make any representations concerning the accuracy, likely results, or reliability of the use of the materials on its Website or otherwise relating to such materials or on any sites linked to this Website.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                4. Limitations
              </h2>
              <p className="text-white/80 leading-relaxed">
                In no event shall The Perth Bookkeeper or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on The Perth Bookkeeper's Website, even if The Perth Bookkeeper or an authorized representative has been notified orally or in writing of the possibility of such damage.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                5. Accuracy of Materials
              </h2>
              <p className="text-white/80 leading-relaxed">
                The materials appearing on The Perth Bookkeeper's Website could include technical, typographical, or photographic errors. The Perth Bookkeeper does not warrant that any of the materials on its Website are accurate, complete, or current. The Perth Bookkeeper may make changes to the materials contained on its Website at any time without notice.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                6. Links
              </h2>
              <p className="text-white/80 leading-relaxed">
                The Perth Bookkeeper has not reviewed all of the sites linked to its Website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by The Perth Bookkeeper of the site. Use of any such linked website is at the user's own risk.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                7. Modifications
              </h2>
              <p className="text-white/80 leading-relaxed">
                The Perth Bookkeeper may revise these Terms and Conditions for its Website at any time without notice. By using this Website, you are agreeing to be bound by the then current version of these Terms and Conditions.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                8. Governing Law
              </h2>
              <p className="text-white/80 leading-relaxed">
                These Terms and Conditions and any separate agreements we provide to render services to you are governed by and construed in accordance with the laws of Western Australia, Australia, and you irrevocably submit to the exclusive jurisdiction of the courts and venues located in Perth, Western Australia.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                9. Contact Information
              </h2>
              <p className="text-white/80 leading-relaxed mb-4">
                If you have any questions about these Terms and Conditions, please contact us at:
              </p>
              <div className="bg-white/5 rounded p-4 border border-white/10">
                <p className="text-white/80">
                  <strong className="text-white">Email:</strong> hello@theperthbookkeeper.com.au
                </p>
                <p className="text-white/80 mt-2">
                  <strong className="text-white">Phone:</strong> 0424 200 785
                </p>
              </div>
            </section>

            <div className="border-t border-white/10 pt-8 mt-10">
              <p className="text-white/50 text-sm">
                Last updated: May 2026
              </p>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => setLocation("/")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[oklch(0.72_0.12_75)] text-white rounded-lg font-medium hover:bg-[oklch(0.72_0.12_75)]/90 transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
